import { createStyles, PasswordInput, type PasswordInputProps } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { type ChangeEventHandler, useState } from 'react';
import { ASSETS_BUCKET_URL } from '@/config';

type PasswordInputRegister = {
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onBlur?: () => void;
  onFocus?: () => void;
  error?: string | undefined;
};

type PasswordFieldProps = PasswordInputProps & {
  label?: string;
  register: PasswordInputRegister;
};

const useStyles = createStyles((theme, { floating }: { floating: boolean }) => ({
  root: {
    position: 'relative',
  },

  label: {
    position: 'absolute',
    zIndex: 2,
    top: 18,
    left: theme.spacing.md,
    pointerEvents: 'none',
    // eslint-disable-next-line no-nested-ternary
    color: floating
      ? theme.colorScheme === 'dark'
        ? theme.white
        : theme.colors.gray[6]
      : theme.colorScheme === 'dark'
      ? theme.colors.dark[3]
      : theme.colors.gray[5],
    transition: 'transform 150ms ease, color 150ms ease, font-size 150ms ease',
    transform: floating ? 'translate(0, -12px)' : 'none',
    fontSize: floating ? theme.fontSizes.md : theme.fontSizes.lg,
    fontWeight: floating ? 400 : 600,
    lineHeight: 1.13,
    letterSpacing: '0.19px',
  },

  required: {
    transition: 'opacity 150ms ease',
    opacity: floating ? 1 : 0,
  },

  input: {
    border: 'none',
    backgroundColor: '#f7f7f7',
    outline: 'none',
    minHeight: 54,
    height: 'auto',
    // width: 328,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: 1.13,
    letterSpacing: '0.19px',
    borderRadius: '8px',
    color: '#212121 !important',
    paddingTop: theme.spacing.md,
    paddingLeft: theme.spacing.md,

    '&::placeholder': {
      transition: 'color 150ms ease',
      color: !floating ? 'transparent' : undefined,
    },
  },
  rightSection: {
    paddingRight: theme.spacing.lg,
  },
  innerInput: {
    minHeight: 54,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: 1.13,
    letterSpacing: '0.19px',
    borderRadius: '8px',
    color: '#212121 !important',
    width: '100%',
    paddingTop: theme.spacing.md,
    paddingLeft: theme.spacing.md,
  },
}));

export const PasswordField = (props: PasswordFieldProps) => {
  const { label, register } = props;

  const [focused, setFocused] = useState(false);

  const [visible, { toggle }] = useDisclosure(false);

  const { value, error, onChange, onBlur, onFocus } = register;
  const { classes } = useStyles({ floating: value.toString().length !== 0 || focused });

  return (
    <PasswordInput
      label={label}
      classNames={classes}
      value={value}
      error={error}
      onChange={onChange}
      // eslint-disable-next-line react/no-unstable-nested-components
      visibilityToggleIcon={({ reveal }) =>
        !reveal ? (
          <img
            src={`${ASSETS_BUCKET_URL}/svg/CloseEye.svg`}
            alt="CloseEye"
            loading="lazy"
            className="text-field-icon"
          />
        ) : (
          <img
            src={`${ASSETS_BUCKET_URL}/svg/OpenEye.svg`}
            alt="OpenEye"
            loading="lazy"
            className="text-field-icon"
          />
        )
      }
      visible={visible}
      onVisibilityChange={toggle}
      onBlur={() => {
        setFocused(false);
        onBlur?.();
      }}
      onFocus={() => {
        setFocused(true);
        onFocus?.();
      }}
      onKeyUp={(e: any) => {
        if (e.key === 'Enter') {
          e.target.blur();
        }
      }}
      mt={12}
      mb={12}
      // {...props}
    />
  );
};
